import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import LineSeperator from '../ui/LineSeperator';
import '../../stylings/VideoGallery.css';

const fallbackVideos = [
  { _id: 'v1', title: 'Fancy Dress Competition',              videoUrl: 'https://youtu.be/GakocQD9S28' },
  { _id: 'v2', title: 'Christmas Celebration',                videoUrl: 'https://youtu.be/mFPuVSQ392Y' },
  { _id: 'v3', title: 'Annual Function – November 2019',      videoUrl: 'https://youtu.be/UMbds2vGShs' },
  { _id: 'v4', title: "Bal Diwas – Children's Day",          videoUrl: 'https://youtu.be/GZ3cX7jahJ0' },
  { _id: 'v5', title: 'Republic Day Celebration – Part 4',    videoUrl: 'https://youtu.be/eaNj80dma2M' },
  { _id: 'v6', title: 'School Cultural Event Highlights',     videoUrl: 'https://youtu.be/us_KpB47R1o' },
];

const SHOWCASE_VIDEO_ID = 'fz33kKPThrU';
const AUTO_SCROLL_SPEED = 0.6; // px per frame

function getYoutubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:embed\/|v=|youtu\.be\/)([^?&"'>]+)/);
  return match ? match[1] : null;
}

function getYoutubeThumbnail(url) {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

export default function VideoGallery() {
  const videos = useSelector((state) => state.videoGallery.videos);
  const displayVideos = videos.length > 0 ? videos : fallbackVideos;

  // Duplicate list for seamless infinite scroll
  const loopedVideos = [...displayVideos, ...displayVideos, ...displayVideos];

  const trackRef    = useRef(null);
  const rafRef      = useRef(null);
  const pausedRef   = useRef(false);   // pause on hover / drag
  const isDragging  = useRef(false);
  const dragStartX  = useRef(0);
  const scrollAtDragStart = useRef(0);

  const [modalVideoId, setModalVideoId] = useState(null);
  const modalRef = useRef(null);

  /* ── Auto-scroll loop ── */
  const tick = useCallback(() => {
    const el = trackRef.current;
    if (el && !pausedRef.current) {
      el.scrollLeft += AUTO_SCROLL_SPEED;
      // When we reach the end of the second copy, jump back to the start of the second copy
      const oneSetWidth = el.scrollWidth / 3;
      if (el.scrollLeft >= oneSetWidth * 2) {
        el.scrollLeft -= oneSetWidth;
      }
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    // Start at the middle copy so we can scroll both ways
    const el = trackRef.current;
    if (el) el.scrollLeft = el.scrollWidth / 3;
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [tick]);

  /* ── Drag-to-scroll ── */
  function onMouseDown(e) {
    isDragging.current = true;
    pausedRef.current  = true;
    dragStartX.current = e.clientX;
    scrollAtDragStart.current = trackRef.current.scrollLeft;
    trackRef.current.style.cursor = 'grabbing';
    e.preventDefault();
  }
  function onMouseMove(e) {
    if (!isDragging.current) return;
    const delta = dragStartX.current - e.clientX;
    trackRef.current.scrollLeft = scrollAtDragStart.current + delta;
  }
  function onMouseUp() {
    isDragging.current = false;
    pausedRef.current  = false;
    if (trackRef.current) trackRef.current.style.cursor = 'grab';
  }

  /* ── Touch drag ── */
  function onTouchStart(e) {
    pausedRef.current  = true;
    dragStartX.current = e.touches[0].clientX;
    scrollAtDragStart.current = trackRef.current.scrollLeft;
  }
  function onTouchMove(e) {
    const delta = dragStartX.current - e.touches[0].clientX;
    trackRef.current.scrollLeft = scrollAtDragStart.current + delta;
  }
  function onTouchEnd() { pausedRef.current = false; }

  /* ── Modal ── */
  function openModal(videoUrl) {
    const id = getYoutubeId(videoUrl);
    if (id) { pausedRef.current = true; setModalVideoId(id); }
  }
  function closeModal() { pausedRef.current = false; setModalVideoId(null); }
  function handleBackdropClick(e) { if (e.target === modalRef.current) closeModal(); }

  return (
    <section className="video-gallery-section">
      <div className="vg-header-area">
        <LineSeperator title="Video Gallery" />
      </div>

      {/* ── Dark background panel + slider ── */}
      <div className="vg-panel">
        {/* fade edges */}
        <div className="vg-fade-left"  />
        <div className="vg-fade-right" />

        <div
          className="vg-scroll-track"
          ref={trackRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseEnter={() => { pausedRef.current = true; }}
        >
          {loopedVideos.map((video, idx) => {
            const thumb = video.thumbnail || getYoutubeThumbnail(video.videoUrl);
            return (
              <button
                key={`${video._id}-${idx}`}
                className="vg-card"
                onClick={() => openModal(video.videoUrl)}
                title={video.title}
                draggable={false}
              >
                <div className="vg-thumb-wrapper">
                  {thumb
                    ? <img src={thumb} alt={video.title} className="vg-thumb" draggable={false} />
                    : <div className="vg-thumb-placeholder" />
                  }
                  <div className="vg-play-overlay">
                    <span className="vg-play-btn">
                      <span className="vg-play-triangle">&#9654;</span>
                    </span>
                  </div>
                </div>
                <p className="vg-card-title">{video.title}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Fixed showcase video ── */}
      <div className="container-fluid px-4 px-md-5">
        <div className="vg-showcase-wrapper mt-4">
          <div className="vg-showcase-embed">
            <iframe
              src={`https://www.youtube.com/embed/${SHOWCASE_VIDEO_ID}?rel=0&modestbranding=1`}
              title="School Showcase Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/videoall" className="section-view-all-btn">View All Videos</Link>
        </div>
      </div>

      {/* ── YouTube Modal ── */}
      {modalVideoId && (
        <div className="vg-modal-backdrop" ref={modalRef} onClick={handleBackdropClick}>
          <div className="vg-modal-box">
            <button className="vg-modal-close" onClick={closeModal} aria-label="Close">&#10005;</button>
            <div className="vg-modal-embed">
              <iframe
                src={`https://www.youtube.com/embed/${modalVideoId}?autoplay=1&rel=0`}
                title="Video Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
